//jul17 routes/index.js
import React, {
  useEffect,
  useState,
  useCallback,
  lazy,
  Suspense 
} from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, Outlet, useLocation } from 'react-router-dom';
//import { onAuthStateChanged, getAuth } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import LoadingScreen from '../components/LoadingScreen/LoadingScreen';
import Login from '../pages/Login';
import HomePagetest from '../pages/HomePagetest'; // Import HomePagetest
import NavbarComponent from '../components/Navbar/NavbarComponent';
import { UserManagementProvider } from '../contexts/UserManagementContext'; // Import the context
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FieldEditProvider } from '../contexts/FieldEditContext';
import { FieldMetadataProvider } from '../contexts/FieldMetadataContext'; // Import FieldMetadataProvider
import { FormLayoutProvider } from '../contexts/FormLayoutContext';
import { FormSubmissionSettingsProvider } from '../contexts/FormSubmissionSettingsContext';
import { MultiAnswerFieldProvider } from '../contexts/MultiAnswerFieldContext'; // Import MultiAnswerFieldProvider
import { FormThemeProvider } from '../contexts/FormThemeContext';
import { DropdownDependenciesProvider } from '../contexts/DropdownDependenciesContext'; // Import DropdownDependenciesProvider
import { FormSaveProvider } from '../contexts/FormSaveContext'; // Import FormSaveProvider
import { FormSubmissionsProvider } from '../contexts/FormSubmissionsContext';
import { FormActionsProvider } from '../contexts/FormActionsContext'; 
import { FormPublishProvider } from '../contexts/FormPublishContext'; // Import FormPublishProvider
import { FormProvider } from '../contexts/FormContext'; // Correct import for FormProvider
import { AssetMetadataProvider } from '../contexts/AssetMetadataContext';
import { OrganizationProvider2 } from '../contexts/OrganizationContext2';
import { BillingsProvider } from '../contexts/BillingContext';
import { FormAnswersProvider } from '../contexts/FormAnswersContext';
import { OrgAssetConfigProvider } from '../contexts/OrgAssetConfigContext';
import { NavbarActionsProvider } from '../contexts/NavbarActionsContext';
import { EmbedRuntimeProvider } from '../contexts/EmbedRuntimeContext';


import PublicFormPreviewWrapper from '../contexts/PublicFormPreviewWrapper';
import InnerPublicFormPreview from '../pages/InnerPublicFormPreview';
import TemplateGallery from '../pages/TemplateGallery';


// Import additional pages (Unauthenticated routes)
import Signup from '../pages/Signup';
import SignupInvite from '../pages/SignupInvite';
import AcceptInvitePage from '../pages/AcceptInvitePage';
import Logout from '../pages/Logout';
import ForgotPassword from '../pages/ForgotPassword';
//import PublicPricingPage from "../pages/PublicPricingPage"; --old pricing page when wordpress didn't exist
import SignupLoadingPage from '../pages/SignupLoadingPage';

// Import settings-related components
import SettingsDashboard from '../pages/SettingsDashboard';
import RequirePermission   from '../components/auth/RequirePermission';
import RouteGuard          from './RouteGuard';
import RequireAccess   from '../components/auth/RequireAccess';


import NoAccess from "../pages/NoAccess";
import UserProfilePage from '../pages/UserProfilePage';
import UserManagementPage from '../pages/UserManagementPage';
import OrganizationSettingsPage from '../pages/OrganizationSettingsPage';
import SmartListsPage from '../pages/SmartListsPage';

import BillingSettingsPage from '../pages/BillingSettingsPage';
//import SubscriptionPage from "../pages/SubscriptionPage"; --old subscription page
import SubscriptionPage2 from "../pages/SubscriptionPage2";

import CheckoutResultPage from '../pages/CheckoutResultPage'; 
import AssetStudioSettingsPage from '../pages/AssetStudioSettingsPage';
import PricingPresentationAdminPage from '../pages/PricingPresentationAdminPage';

import PublishedFormPage from "../pages/PublishedFormPage";
import IntegrationsPage from '../components/Integrations/IntegrationsPage';
import AssetDetailsView from '../pages/AssetDetailsView';
import AssetLibraryPage from '../pages/AssetLibraryPage';
import AssetDetailsDialog from '../AssetManager/AssetDetailsDialog';
import AdminBillingOpsPage from "../pages/AdminBillingOpsPage";


// Import Form-builder components
import FormBuilderWrapper from '../contexts/FormBuilderWrapper';
import FormDesignPage from '../pages/FormDesignPage';
import FormLayout from '../pages/FormLayout';
import FormListsPage from '../pages/FormListsPage';
import FormListsLabPage from '../pages/FormListsLabPage';
import SubmitOptionsBuilderPage from '../pages/SubmitOptionsBuilderPage';
import FormSettingsPage from '../pages/FormSettingsPage';
import FormPreview from '../components/FormSettings/FormPreview';
import SubmissionsPage from '../pages/SubmissionsPage';

// Modal UI for AUF overlay
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

//Tawk chat provider
import { ChatProvider } from "../contexts/ChatContext";
import ChatBoot from "../components/chat/ChatBoot";

const TermsAndConditions = lazy(
  /* webpackChunkName: "terms" */ () =>
    import('../pages/TermsAndConditions')
);

const PrivacyPolicy = lazy(
  /* webpackChunkName: "privacy" */ () => import("../pages/PrivacyPolicy")
);

const GdprDeclarations = lazy(
  /* webpackChunkName: "gdpr" */ () => import("../pages/GdprDeclarations")
);

const EmbeddedPublishedFormPage = lazy(
  /* webpackChunkName: "embed-published-form" */ () =>
    import("../pages/EmbeddedPublishedFormPage")
);

// -----------------------------------------------------------------------------
// Embed route Suspense fallback
//
// PURPOSE
//  • Used only while the EmbeddedPublishedFormPage code-split chunk is loading.
//  • Prevents the global app LoadingScreen/Backdrop from appearing inside iframes.
//  • Keeps the iframe visually transparent so the parent microsite owns the page
//    background during the brief lazy-load window.
//  • Provides an accessible status via aria-label without rendering visible text.
//
// IMPORTANT
//  • EmbeddedPublishedFormPage owns the mounted-route document/body transparency.
//  • PublishedFormCanvas owns the ready-state embed canvas transparency.
//  • This fallback only covers the lazy import boundary.
// -----------------------------------------------------------------------------
const EmbeddedRouteSuspenseFallback = React.memo(() => (
  <div
    data-testid="utm-smart-manager-embed-suspense-fallback"
    aria-busy="true"
    aria-label="Loading embedded form"
    role="status"
    style={{
      display: "block",
      width: "100%",
      minHeight: 0,
      overflow: "visible",
      boxSizing: "border-box",
      background: "transparent",
    }}
  />
));

/* ──────────────────────────────────────────────────────────────────
 * Helper: OR-gated permission wrapper for DAM upload-form workflow.
 * Only used inside authenticated settings routes.
 * ────────────────────────────────────────────────────────────────── */
const RequireAnyDamUploadPerm = ({ children }) => {
  const { hasPermission, initialized } = useAuth();
  if (!initialized) return <LoadingScreen />;

  const ok =
    hasPermission('createUploadAssetForm')  ||
    hasPermission('editUploadAssetForm')    ||
    hasPermission('publishUploadAssetForm') ||
    hasPermission('deleteUploadAssetForm'); // optional

  return ok ? children : <Navigate to="/no-access" replace />;
};

// Authenticated Route Component
const AuthenticatedRoutes = React.memo(({ user, loading }) => {
//  console.log('AuthenticatedRoutes loading state:', loading); // Log loading state
  if (loading || user === undefined) {
//    console.log('Rendering LoadingScreen in AuthenticatedRoutes'); // Track when LoadingScreen is rendered
    return <LoadingScreen />;
  }

  if (user) {
    return (
      <NavbarActionsProvider>
        <ChatProvider>
          <ChatBoot />
          <NavbarConditionalWrapper currentUser={user} />
          <Outlet />
        </ChatProvider>
      </NavbarActionsProvider>
    );
  }

  return <Navigate to="/login" replace />;
});


// Unauthenticated Route Component
const UnauthenticatedRoutes = React.memo(({ user, loading }) => {
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  // If a user is already logged in, return them to the intended route
  // when they were sent here from RequireAccess (eg /forms/:formId).
  // Fallback stays "/" for normal "go to /login while already logged in" behavior.
  if (user) {
    const redirectTo = location.state?.redirectTo || '/';
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
});


// NavbarConditionalWrapper to conditionally render NavbarComponent or FormBuilderNavbarComponent
const NavbarConditionalWrapper = ({ currentUser }) => {
  const location = useLocation();

  // If inside form-builder preview mode, don't render any navbar
  if (location.pathname.match(/\/form-builder\/[^/]+\/preview/)) {
    return null;
  }

  // If inside form-builder (but not preview), let FormBuilderWrapper handle the navbar
  if (location.pathname.includes("/form-builder/")) {
    return null;
  }

  // Render the main app navbar for all other routes
  return <NavbarComponent currentUser={currentUser} />;
};

const Index2 = () => {
  const { currentUser, authLoading, initialized } = useAuth();

  // IMPORTANT:
  // - We should NOT block the router on `initialized`.
  // - `initialized` is an app-level flag that can lag behind auth resolution.
  // - The only state that must be resolved before showing routes is:
  //     currentUser !== undefined
  //
  // Firebase rule:
  //   undefined = auth not resolved yet
  //   null      = signed out
  //   object    = signed in
  if (currentUser === undefined) {
    return <LoadingScreen />;
  }

  return (
    <Router>
      <AppRoutes
        user={currentUser}     // null or user object
        initialized={initialized}
        loading={false}        // no local loading now
        authLoading={authLoading}
      />
    </Router>
  );
};


/* -------------------------------------------------------------------
   AppRoutes
   - Renders your full Routes tree as the background (UNCHANGED)
   - When we navigated with navigate(path, { state: { modalFrom } }),
     it adds a second tiny <Routes> that overlays the AUF dialog.
   ------------------------------------------------------------------- */
function AppRoutes({ user, initialized, loading, authLoading }) {
  const location = useLocation();
  // If we navigated with { state: { modalFrom } }, keep the old location
  // as the "background" to show the AUF dialog over it.
  const modalFrom = location.state && location.state.modalFrom ? location.state.modalFrom : null;

  return (
    <>
      {/* BACKGROUND ROUTES — your entire tree, UNCHANGED */}
      <Routes location={modalFrom || location}>
        {/* PUBLIC PREVIEW ROUTE - outside of auth-protected routes for screenshot capture */}
        <Route
          path="/public-preview/:formId"
          element={<PublicFormPreviewWrapper />}  // ✅ Correct
        />

        <Route path="/no-access" element={<NoAccess />} />

        {/* ──────────────────────────────────────────────────────────────── */}
        {/*  Signup Bootstrap Route                                          */}
        {/*  Must be reachable even before profile hydration (accounts/{uid}) */}
        {/* ──────────────────────────────────────────────────────────────── */}
        <Route
          path="/loadingsignup"
          element={
            <OrganizationProvider2>
              <FormProvider>
                <SignupLoadingPage />
              </FormProvider>
            </OrganizationProvider2>
          }
        />
        
        {/* Unauthenticated routes */}
        <Route
          element={
            <UnauthenticatedRoutes
              user={user}
              // IMPORTANT:
              // Unauthenticated routes should ONLY wait for Firebase auth resolution,
              // not for `initialized` (which can lag in incognito / empty cache).
              loading={loading || authLoading || user === undefined}
            />
          }
        >


          <Route path="/login" element={<Login />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />

          <Route
            path="/signup"
            element={
              <OrganizationProvider2>
                <Signup />
              </OrganizationProvider2>
            }
          />        
          <Route path="/signup/invite" element={<SignupInvite />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/terms-and-conditions"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <TermsAndConditions />
              </Suspense>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <PrivacyPolicy />
              </Suspense>
            }
          />
          <Route
            path="/gdpr-declarations"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <GdprDeclarations />
              </Suspense>
            }
          />
        </Route>

        <Route
          path="/forms/:formId"
          element={
   <UserManagementProvider>
            <FormProvider>
              <RequireAccess>
              <FormThemeProvider>
                <FieldMetadataProvider>
                  <FormLayoutProvider>
                    <FieldEditProvider>
                      <MultiAnswerFieldProvider>
                      <FormAnswersProvider>
                        <DropdownDependenciesProvider>
                          <FormSubmissionSettingsProvider>
                            <FormActionsProvider>
                              <FormSubmissionsProvider>
                                <FormSaveProvider>
                                  <FormPublishProvider>
                                    <AssetMetadataProvider>
                                      <OrgAssetConfigProvider
                                        providerFlags={{}} 
                                        staticPolicy={{ includeAssetId: true }}
                                      >
                                        <Outlet />
                                      </OrgAssetConfigProvider>
                                    </AssetMetadataProvider>
                                  </FormPublishProvider>
                                </FormSaveProvider>
                              </FormSubmissionsProvider>
                            </FormActionsProvider>
                          </FormSubmissionSettingsProvider>
                        </DropdownDependenciesProvider>
                       </FormAnswersProvider>
                      </MultiAnswerFieldProvider>
                    </FieldEditProvider>
                  </FormLayoutProvider>
                </FieldMetadataProvider>
              </FormThemeProvider>
             </RequireAccess>

            </FormProvider>
   </UserManagementProvider>

          }
        >
          {/* /forms/:formId => PublishedFormPage (public or private logic is inside that page) */}
          <Route index element={<PublishedFormPage />} />
        </Route>

        {/* ------------------------------------------------------------------- */}
        {/*  PUBLIC EMBED ROUTE                                                  */}
        {/* ------------------------------------------------------------------- */}
        {/*
          /embed/forms/:formId
          - Dedicated iframe-friendly public form runtime.
          - Keeps /forms/:formId unchanged as the standalone public destination.
          - Does NOT mount FormBuilderWrapper, DndProvider, authenticated app shell,
            dashboard navbar, chat, billing wrappers, template onboarding, AUF restore
            dialogs, or DAM/AUF registry hydration.
          - Reuses the canonical public form runtime providers so layout, theme,
            metadata, dropdowns, dependencies, submission settings, validation,
            generated output, and public submissions remain schema-aligned.
          - FormAnswersProvider is explicitly non-persistent for public iframe use.
          - FormSaveProvider and FormPublishProvider are intentionally omitted because
            this route never saves drafts or publishes forms.
          - AssetMetadataProvider and OrgAssetConfigProvider are intentionally omitted
            for the initial microsite POC; AssetMetadataContext has safe defaults, and
            asset/AUF interactions will be handled later through a locked premium state.
        */}
        <Route
          path="/embed/forms/:formId"
          element={
            <EmbedRuntimeProvider>
              <FormProvider>
                <FormThemeProvider>
                  <FieldMetadataProvider>
                    <FormLayoutProvider>
                      <FieldEditProvider>
                        <MultiAnswerFieldProvider>
                          <FormAnswersProvider persist={false}>
                            <DropdownDependenciesProvider>
                              <FormSubmissionSettingsProvider>
                                <FormActionsProvider>
                                  <FormSubmissionsProvider>
                                    <Suspense fallback={<EmbeddedRouteSuspenseFallback />}>
                                      <EmbeddedPublishedFormPage />
                                    </Suspense>
                                  </FormSubmissionsProvider>
                                </FormActionsProvider>
                              </FormSubmissionSettingsProvider>
                            </DropdownDependenciesProvider>
                          </FormAnswersProvider>
                        </MultiAnswerFieldProvider>
                      </FieldEditProvider>
                    </FormLayoutProvider>
                  </FieldMetadataProvider>
                </FormThemeProvider>
              </FormProvider>
            </EmbedRuntimeProvider>
          }
        />
        
/* ------------------------------------------------------------------- */
/*  AUTHENTICATED ROUTES                                               */
/* ------------------------------------------------------------------- */
        

        
/* ------------------------------------------------------------------- */
/*  AUTHENTICATED ROUTES                                               */
/* ------------------------------------------------------------------- */
<Route
  element={
    <OrganizationProvider2>
      <BillingsProvider>
      <AuthenticatedRoutes
        user={user}
        // Auth routes only need auth resolution:
        loading={loading || authLoading || user === undefined}
      />
      </BillingsProvider>
    </OrganizationProvider2>
  }
>

  {/* ──────────────────────────────────────────────────────────────── */}
  {/*  ROUTES THAT **MUST** STAY OPEN EVEN IF THE ACCOUNT IS BLOCKED  */}
  {/* ──────────────────────────────────────────────────────────────── */}

  {/* 1) Subscription / upgrade page (user needs access to pay) */}
  <Route
    path="/subscription"
    element={
          <SubscriptionPage2 />
    }
  />

  {/* 2) Stripe Checkout success / cancel return */}
  <Route
    path="/billing/checkout-result"
    element={
          <CheckoutResultPage />
    }
  />

  <Route
    path="/admin/billing-ops"
    element={
      <RequirePermission
        permissions={["configureBilling", "manageBilling"]}
        any
      >
        <AdminBillingOpsPage />
      </RequirePermission>
    }
  />
  
  {/* 3) Settings pages that must remain reachable
        (my-profile, org-settings, billing-settings)               */}
  <Route
    path="/settings"
    element={
        <SettingsDashboard />
    }
  >
    {/* 3a My profile — always accessible */}
    <Route path="my-profile" element={<UserProfilePage />} />

    {/* 3b) Organization settings — always accessible */}
    <Route
      path="organization-settings"
      element={
        <RequirePermission permissions="manageOrganization">
          <OrganizationSettingsPage />
        </RequirePermission>
      }
    />

    <Route
      path="smartlists"
      element={
        <RequirePermission permissions="manageOrganization">
          <SmartListsPage />
        </RequirePermission>
      }
    />

    {/* 3c) Billing settings — always accessible */}
    <Route
      path="billing-settings"
      element={
        <RequirePermission permissions={['manageBilling', 'viewInvoices']}>
            <BillingSettingsPage />
        </RequirePermission>
      }
    />
  {/* 3d) User-management (💳 blocked) */}
  <Route
    path="user-management"
    element={
      <RequirePermission permissions="manageUsers">
          <UserManagementProvider>
            <UserManagementPage />
          </UserManagementProvider>
      </RequirePermission>
    }
  />

    {/* 3e) Asset Studio (DAM-lite) — OR-gated on DAM-form perms; authenticated scope */}
    <Route
      path="asset-studio"
      element={
        <RequireAnyDamUploadPerm>
          <AssetStudioSettingsPage />
        </RequireAnyDamUploadPerm>
      }
    />

    {/* index fallback still shows profile */}
    <Route index element={<UserProfilePage />} />
  </Route>

  {/* ──────────────────────────────────────────────────────────────── */}
  {/*  EVERY OTHER AUTHENTICATED ROUTE PASSES THROUGH ROUTEGUARD      */}
  {/* ──────────────────────────────────────────────────────────────── */}
  <Route element={<RouteGuard />}>
    {/* ----- Home & Template Gallery (provider stack unchanged) ----- */}
    <Route
      element={
          <UserManagementProvider>
            <FormProvider>
              <Outlet />
            </FormProvider>
          </UserManagementProvider>
      }
    >
      <Route path="/" element={<HomePagetest />} />
      <Route path="/template-gallery" element={<TemplateGallery />} />
      <Route path="/assets" element={<AssetLibraryPage />} />
    </Route>



    {/* ---------------- Integrations (blocked) --------------------- */}
    <Route
      path="/integrations"
      element={
          <IntegrationsPage />
      }
    />

    {/* ---------------- Asset Details (blocked) --------------------- */}
    <Route
      path="/assets/:assetId"
      element={
        // Keep this wrapper light; AssetDetailsView mounts its own AUF runtime providers
        <OrganizationProvider2>
          <UserManagementProvider>
            <FormProvider>
              <AssetDetailsView />
            </FormProvider>
          </UserManagementProvider>
        </OrganizationProvider2>
      }
    />

    {/* ---- Pricing Presentation Admin (superAppAdmin-gated in page) ---- */}
    <Route
      path="/admin/pricing-presentation"
      element={<PricingPresentationAdminPage />}
    />

    {/* ---- Submissions (blocked when billing bad) ----------------- */}
    <Route
      path="/forms/:formId/submissions"
      element={
        <FormProvider>
          <FormThemeProvider>
            <FieldMetadataProvider>
              <FormLayoutProvider>
                <FieldEditProvider>
                  <MultiAnswerFieldProvider>
                   <FormAnswersProvider>
                    <DropdownDependenciesProvider>
                      <FormSubmissionSettingsProvider>
                        <FormActionsProvider>
                          <FormSubmissionsProvider>
                            <FormSaveProvider>
                              <FormPublishProvider>
                                  <AssetMetadataProvider>

                                 <OrgAssetConfigProvider
                                   providerFlags={{}} 
                                   staticPolicy={{ includeAssetId: true }}
                                 >
                                    <SubmissionsPage />
                                 </OrgAssetConfigProvider>
                                    </AssetMetadataProvider>

                              </FormPublishProvider>
                            </FormSaveProvider>
                          </FormSubmissionsProvider>
                        </FormActionsProvider>
                      </FormSubmissionSettingsProvider>
                    </DropdownDependenciesProvider>
                   </FormAnswersProvider>
                  </MultiAnswerFieldProvider>
                </FieldEditProvider>
              </FormLayoutProvider>
            </FieldMetadataProvider>
          </FormThemeProvider>
        </FormProvider>
      }
    />

    {/* -------------- Form-builder stack (blocked) ---------------- */}
    <Route
      path="/form-builder/:formId"
      element={
        <DndProvider backend={HTML5Backend}>
          <FormBuilderWrapper />
        </DndProvider>
      }
    >
<Route index element={<FormDesignPage />} />
<Route path="design" element={<FormDesignPage />} />
<Route path="layout" element={<FormLayout />} />

{/* ---------------------------------------------------------------------------
 * Lists V2 replacement
 * ---------------------------------------------------------------------------
 * The production /lists route now renders the shared Spreadsheet-engine
 * implementation.
 *
 * WHY
 *  • Keeps the route under the existing FormBuilderWrapper shell.
 *  • Preserves the FormBuilderNavbarComponent and FormBuilderToolbar.
 *  • Avoids introducing a separate route tree or provider stack.
 *  • Keeps Save Draft / Publish using the existing FormListsDraftSyncProvider
 *    already mounted by FormBuilderWrapper.
 *
 * TRANSITION NOTE
 *  • Keep /lists-lab temporarily as an alias during rollout so any existing
 *    dev/bookmarked links still work.
 *  • After QA, remove the /lists-lab alias.
 */}
<Route path="lists" element={<FormListsLabPage />} />
<Route path="lists-lab" element={<FormListsLabPage />} />

<Route path="submit-options" element={<SubmitOptionsBuilderPage />} />
<Route path="settings" element={<FormSettingsPage />} />
<Route path="preview" element={<FormPreview />} />
    </Route>

    {/* (add any further authenticated routes here, all protected) */}
  </Route> 
</Route> 

      </Routes>

      {/* MODAL OVERLAY — only renders when we navigated with state.modalFrom */}
      {modalFrom && (
        <Routes>
          {/* Overlay AUF route (/forms/:formId) as a Dialog on top of the background */}
          <Route path="/forms/:formId" element={<AufDialog />} />
         {/* Overlay Asset Details (/assets/:assetId) as a Dialog on top of the background */}
         <Route path="/assets/:assetId" element={<AssetDetailsDialog />} />

        </Routes>
      )}
    </>
  );
}

/* -------------------------------------------------------------------
   AufDialog
   - Responsive MUI Dialog (desktop-first), ~90–98% viewport
   - Uses dvh on mobile to account for browser chrome
   - Renders PublishedFormPage with the SAME provider stack as /forms/:formId
   - PublishedFormPage should hide its Navbar in modal (via location.state?.modalFrom)
   ------------------------------------------------------------------- */
function AufDialog() {
  const navigate = useNavigate();
  const handleClose = () => navigate(-1);

  return (
    <Dialog
      open
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          /* Desktop-first, but responsive:
             - On very small screens we nearly full-screen (98vw x 96dvh)
             - On medium/large desktop, keep a comfortable frame around the dialog
             - Use dvh so mobile browser UI doesn’t clip vertical space */
          width:  { xs: '98vw', sm: '96vw', md: '94vw', lg: '92vw', xl: '90vw' },
          height: { xs: '96dvh', sm: '96dvh', md: '94dvh', lg: '92dvh', xl: '90dvh' },

          /* Safety caps in rare environments */
          maxWidth:  '98vw',
          maxHeight: '98dvh',

          overflow: 'hidden',
          borderRadius: { xs: 2, md: 3 }, // slightly stronger rounding on desktop
        },
      }}
    >
      {/* Close button */}
      <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
        <IconButton onClick={handleClose} aria-label="Close AUF dialog">
          <CloseIcon />
        </IconButton>
      </div>

      {/* AUF content with the same providers as /forms/:formId */}
      <div style={{ height: '100%', overflow: 'auto' }}>
        <UserManagementProvider>
          <FormProvider>
            <RequireAccess>
              <FormThemeProvider>
                <FieldMetadataProvider>
                  <FormLayoutProvider>
                    <FieldEditProvider>
                      <MultiAnswerFieldProvider>
                        <FormAnswersProvider>
                          <DropdownDependenciesProvider>
                            <FormSubmissionSettingsProvider>
                              <FormActionsProvider>
                                <FormSubmissionsProvider>
                                  <FormSaveProvider>
                                    <FormPublishProvider>
                                      <AssetMetadataProvider>
                                        <OrgAssetConfigProvider
                                          providerFlags={{}} 
                                          staticPolicy={{ includeAssetId: true }}
                                        >
                                          <PublishedFormPage />
                                        </OrgAssetConfigProvider>
                                      </AssetMetadataProvider>
                                    </FormPublishProvider>
                                  </FormSaveProvider>
                                </FormSubmissionsProvider>
                              </FormActionsProvider>
                            </FormSubmissionSettingsProvider>
                          </DropdownDependenciesProvider>
                        </FormAnswersProvider>
                      </MultiAnswerFieldProvider>
                    </FieldEditProvider>
                  </FormLayoutProvider>
                </FieldMetadataProvider>
              </FormThemeProvider>
            </RequireAccess>
          </FormProvider>
        </UserManagementProvider>
      </div>
    </Dialog>
  );
}

export default Index2;

